import {
  INVOICE_REPORT_TYPE,
  HMS_REPORT_TEMPLATE_TYPE,
} from "../../core/constants/erpConstants";
import { ORG_CODES } from "../../shared/constants";
import { getInvoiceAdapter } from "../invoice/adapters";
import { getLineItemRenderer } from "../invoice/lineItems/rendererResolver";

// import ExcelJS from "exceljs";
/* -------------------------------------------------------------
   ✅ Helper: Format date in local time (for display)
------------------------------------------------------------- */
const formatDate = (dateInput) => {
  if (!dateInput) return "";

  let date;
  if (typeof dateInput === "string") {
    let normalized = dateInput.replace("IST", "+0530");
    if (!normalized.includes("T")) normalized += "T00:00:00";
    date = new Date(normalized);
  } else if (dateInput instanceof Date) {
    date = dateInput;
  } else {
    return "";
  }

  if (isNaN(date)) return "";

  const isDateOnly =
    typeof dateInput === "string" &&
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;

  const pad = (n) => (n < 10 ? "0" + n : n);
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();

  if (isDateOnly) return `${day}/${month}/${year}`;

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const displayHours = hours % 12 || 12;
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedTime = `${pad(displayHours)}:${pad(minutes)} ${ampm}`;

  return `${day}/${month}/${year} ${formattedTime}`;
};

/* -------------------------------------------------------------
   ✅ Specific Formatter for Bill Date
------------------------------------------------------------- */
const formatBillDate = (dateInput) => {
  if (!dateInput) return "";

  let date;
  if (typeof dateInput === "string") {
    if (dateInput.includes("IST")) {
      const fixed = dateInput.replace("IST", "GMT+0530");
      date = new Date(fixed);
    } else {
      let normalized = dateInput;
      if (!normalized.includes("T")) normalized += "T00:00:00";
      date = new Date(normalized);
    }
  } else if (dateInput instanceof Date) {
    date = dateInput;
  } else {
    return "";
  }

  if (isNaN(date)) return "";

  const pad = (n) => (n < 10 ? "0" + n : n);
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const displayHours = hours % 12 || 12;
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedTime = `${pad(displayHours)}:${pad(minutes)} ${ampm}`;

  return `${day}/${month}/${year} ${formattedTime}`;
};

const buildAllInvoiceSummaryHtml = (dtoList) => {
  if (!dtoList || dtoList.length === 0) return "";

  let rowsHtml = dtoList
    .map((dto) => {
      const cust = dto.customerDetail || {};
      const summary = dto.billSummary || {};

      return `
        <tr>
          <td>${cust.section || ""}</td>
          <td>${cust.billNumber || ""}</td>
          <td class="text-right">${
            summary.billAmount?.toFixed(2) || "0.00"
          }</td>
        </tr>`;
    })
    .join("");

  const totalAmount = dtoList.reduce(
    (sum, d) => sum + (d.billSummary?.billAmount || 0),
    0,
  );

  return `
    <div class="bill-block" style="margin-top:20px;">
      <h4 style="margin:0 0 8px 0;">Net Total:</h4>

      <table class="bordered" style="width:100%; font-size:12px;">
        <thead>
          <tr>
            <th>Department</th>
            <th>Bill No.</th>
            <th>Amt (Rs)</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
          <tr>
            <td colspan="2" class="text-right"><b>Total Bill Amount (Rs):</b></td>
            <td class="text-right"><b>${totalAmount.toFixed(2)}</b></td>
          </tr>
        </tbody>
      </table>
    </div>
  `;
};

const buildReceiptSectionHtml = (rows = []) => {
  if (!rows.length) return "";

  const receiptRowsHtml = rows
    .map((r, index) => {
      const typeLabel =
        r.type === "ADVANCE_PAYMENTS"
          ? "Advance Receipt"
          : "Refund Receipt";

      return `
        <tr>
          <td class="text-center">${index + 1}</td>
          <td>${typeLabel}</td>
          <td class="text-center">${r.invoiceDate || ""}</td>
          <td class="text-right">${r.receivedAmt || ""}</td>
          <td>${r.generatedBy || ""}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <div style="margin-top:30px;">
      <h3 style="text-align:center; text-decoration:underline;">
        Receipt Details
      </h3>

      <table class="bordered mt-10">
        <thead>
          <tr>
            <th>Sl No</th>
            <th>Type</th>
            <th>Date</th>
            <th>Amount (Rs)</th>
            <th>Received By</th>
          </tr>
        </thead>
        <tbody>
          ${receiptRowsHtml}
        </tbody>
      </table>
    </div>
  `;
};

/* -------------------------------------------------------------
   ✅ Main HTML Generator
------------------------------------------------------------- */

// export const generateBillHtml = async (dtoList, reportType, orgCode) => {
//   if (!Array.isArray(dtoList)) {
//     dtoList = [dtoList];
//   }

//   let finalHtml = "";
//   for (let i = 0; i < dtoList.length; i++) {
//     const dto = dtoList[i];

//     const singleHtml = await generateSingleBillTemplate(dto, reportType, orgCode);

//     finalHtml += `
//       <div class="invoice-page">
//         ${singleHtml}
//       </div>
//       ${i < dtoList.length - 1 ? `<div style="page-break-after: always;"></div>` : ""}
//     `;
//   }

//   return finalHtml;
// };

export const generateBillHtml = async (dtoList, reportType, orgCode, advanceRefundRows = []) => {
  // Always normalise to array
  orgCode = orgCode ? orgCode : localStorage.getItem("orgCode");
  if (!Array.isArray(dtoList)) {
    dtoList = [dtoList];
  }

  // 🔹 1) MULTI-BILL (Annexure style) – one header, many bills
  if (
    dtoList.length > 0 &&
    reportType === HMS_REPORT_TEMPLATE_TYPE.PRINT_ALL_INVOICE
  ) {
    return await generateMultiBillTemplate(dtoList, orgCode, advanceRefundRows);
  }

  if (reportType === HMS_REPORT_TEMPLATE_TYPE.PRINT_INVOICE_SUMMARY) {
    return await generateSummaryTemplate(dtoList, orgCode);
  }
  // 🔹 2) Existing behaviour – each dto is a full single bill page
  let finalHtml = "";
  for (let i = 0; i < dtoList.length; i++) {
    const dto = dtoList[i];
    const singleHtml = await generateSingleBillTemplate(
      dto,
      reportType,
      orgCode,
    );

    finalHtml += `
      <div class="invoice-page">
        ${singleHtml}
      </div>
      ${
        i < dtoList.length - 1
          ? `<div style="page-break-after: always;"></div>`
          : ""
      }
    `;
  }

  return finalHtml;
};

export const generateSingleBillTemplate = async (dto, reportType, orgCode) => {
  console.log("Generating single bill template", dto, orgCode, reportType);
  const orgPrefix = (orgCode || "MNH").toLowerCase();

  const getTemplateSuffix = (type) => {
    if (
      type === HMS_REPORT_TEMPLATE_TYPE.CONSOLIDATED_INVOICE ||
      type === HMS_REPORT_TEMPLATE_TYPE.ESTIMATE_INVOICE
    ) {
      return "finalinvoice";
    }

    if (
      type === HMS_REPORT_TEMPLATE_TYPE.RECEIPT ||
      type === HMS_REPORT_TEMPLATE_TYPE.REFUND_VOUCHER
    ) {
      return type.toLowerCase();
    }

    return "singleinvoice";
  };

  const templateSuffix = getTemplateSuffix(reportType);
  const templatePath = `/templates/${orgPrefix}_${templateSuffix}Template.html`;

  /* ---------------------------------------------------------
     🔹 Load Template (Fallback to MNH if missing)
  --------------------------------------------------------- */
  let response;
  try {
    response = await fetch(templatePath);
    if (!response.ok) throw new Error(`Template not found: ${templatePath}`);
  } catch (err) {
    console.warn(`Falling back to MNH template: ${err.message}`);
    const fallbackPath = `/templates/mnh_${templateSuffix}Template.html`;
    response = await fetch(fallbackPath);
  }

  let template = await response.text();

  /* ---------------------------------------------------------
     🔹 Handle Receipt / Refund Voucher Templates
  --------------------------------------------------------- */
  if (
    reportType === HMS_REPORT_TEMPLATE_TYPE.RECEIPT ||
    reportType === HMS_REPORT_TEMPLATE_TYPE.REFUND_VOUCHER
  ) {
    const recNo = dto.voucherNo || dto.recNo || "";
    const dateTime = formatDate(dto.paymentDate || "");
    const patientName = dto.patientName || "";
    const regNo = dto.regNo || "";
    const totalAmount = dto.totalAmount?.toFixed(2) || "";
    const amountWords = dto.paymentAmountInWords || "";
    const paymentMode = dto.paymentMode || "";
    const counter = dto.paymentCounter || "";
    const generatedBy = dto.generatedBy || "";
    const printedTime = formatDate(new Date());
    const particulars =
      reportType === HMS_REPORT_TEMPLATE_TYPE.REFUND_VOUCHER
        ? "Towards Advance Refund"
        : "Towards Advance Payment";
    const address = dto?.address || "";
    const phono = dto?.phono || "";
    const description = dto?.description || "";

    template = template
      .replace(/{{recNo}}/g, recNo)
      .replace(/{{dateTime}}/g, dateTime)
      .replace(/{{patientName}}/g, patientName)
      .replace(/{{regNo}}/g, regNo)
      .replace(/{{totalAmount}}/g, totalAmount)
      .replace(/{{amountWords}}/g, amountWords)
      .replace(/{{paymentMode}}/g, paymentMode)
      .replace(/{{counter}}/g, counter)
      .replace(/{{generatedBy}}/g, generatedBy)
      .replace(/{{printedTime}}/g, printedTime)
      .replace(/{{particulars}}/g, particulars)
      .replace(/{{address}}/g, address || "")
      .replace(/{{phono}}/g, phono || "")
      .replace(/{{description}}/g, description || "");

    return template;
  }

  /* ---------------------------------------------------------
     🔹 Handle Invoice Templates
  --------------------------------------------------------- */
  const renderItems = getLineItemRenderer(
    orgCode ? orgCode.toLowerCase() : "default",
  );
  const adaptInvoice = getInvoiceAdapter(
    orgCode ? orgCode.toLowerCase() : "default",
    dto,
  );
  console.log("raw DTO:", dto);
console.log("adaptInvoice:", adaptInvoice);
console.log("narration:", adaptInvoice.narration, "narration1:", adaptInvoice.narration1);
const narration = adaptInvoice.narration || "";
const narration1 = adaptInvoice.narration1 || "";
  const reportName = adaptInvoice.reportName || "";
  const firm = adaptInvoice.firmInfo || {};
  const customer = adaptInvoice.customerDetail || {};
  const billItems = adaptInvoice.billingItems || [];
  const summary = adaptInvoice.billSummary || {};
  const doa = customer.doa && customer.doa !== "null" ? customer.doa : "";
  const dod = customer.dod && customer.dod !== "null" ? customer.dod : "";
  /* ---------------------------------------------------------
   🔹 Dynamic DOA / DOD / Visit Date row
--------------------------------------------------------- */
  let admissionVisitHtml = "";

  const isIp = (customer.patientType || "").toLowerCase() === "inpatient";
  const isOp = (customer.patientType || "").toLowerCase() === "outpatient";

  if (isIp) {
    admissionVisitHtml = `
    <tr>
      <td><b>DOA</b></td>
      <td>: ${doa}</td>
    </tr>
    <tr>
      <td><b>DOD</b></td>
      <td>: ${dod}</td>
    </tr>`;
  } else if (isOp) {
    admissionVisitHtml = `
    <tr>
      <td><b>Visit Date</b></td>
      <td>: ${doa}</td>
    </tr>`;
  } else if (doa || dod) {
    admissionVisitHtml = `
    ${doa ? `<tr><td><b>DOA</b></td><td>: ${doa}</td></tr>` : ""}
    ${dod ? `<tr><td><b>DOD</b></td><td>: ${dod}</td></tr>` : ""}`;
  }

  /* 🔹 IP Number Row */
  const showIpNo = customer.patientType === "InPatient";
  const showOpNo = customer.patientType === "OutPatient";
  const ipNoRow = showIpNo
    ? `
      <tr>
        <td><b>IP No</b></td>
        <td>: ${customer.ipNumber || ""}</td>
      </tr>`
    : showOpNo
      ? `
      <tr>
        <td><b>OP No</b></td>
        <td>: ${customer.ipNumber || ""}</td>
      </tr>`
      : "";

  /* 🔹 Insurance Details */
  let insuranceDetails = "";
  if (customer.insPlan && customer.insPlan.trim() !== "") {
    insuranceDetails = `
      ${
        customer.insurancePolicyNumber
          ? `
      <tr>
        <td><b>Policy No</b></td>
        <td>: ${customer.insurancePolicyNumber}</td>
      </tr>`
          : ""
      }
      ${
        customer.insuranceCardNumber
          ? `
      <tr>
        <td><b>Card No</b></td>
        <td>: ${customer.insuranceCardNumber}</td>
      </tr>`
          : ""
      }`;
  }

  /* 🔹 Tax Row */
  /* 🔹 Show Tax Amount Row (Bottom Section) */
  const showTaxAmount = summary.tax !== undefined && summary.tax !== null && summary.tax > 0 && customer?.section === "PHARMACY"; 
    // customer?.patientType !== "InPatient" && customer?.section === "PHARMACY";
  const advancePaymentAmount =
    summary?.advancePayment !== null && summary?.advancePayment !== undefined
      ? `
      <tr>
        <td class="label">Amount Received Towards Advance (Rs) :</td>
        <td class="value">${Number(summary.advancePayment).toFixed(2)}</td>
      </tr>
    `
      : "";
  const taxAmount = showTaxAmount
    ? `
    <tr>
      <td class="label">Tax (Rs) :</td>
      <td class="value">${summary?.tax?.toFixed(2) || ""}</td>
    </tr>`
    : "";
  const balanceAmount = summary?.payable;

  const balanceAndRefundRow =
    balanceAmount !== undefined && balanceAmount !== null
      ? `
    <tr>
      <td class="label">${balanceAmount >= 0 ? "Balance Amount (Rs) :" : "Refund Amount (Rs) :"}</td>
      <td class="value">
        ${Math.abs(balanceAmount).toFixed(2)}
      </td>
    </tr>`
      : "";

  /* 🔹 Service Items Table */

  /* 🔹 Final Table HTML */
  const finalTableHtml = renderItems(billItems, showTaxAmount);

  /* 🔹 Payment Details (Org based via Adapter) */
  const paymentList = summary?.paymentList || [];

  // const paymentRows = paymentList.length
  //   ? paymentList
  //       .map(
  //         (p) => `
  //       <tr>
  //         <td class="center">${p?.paymentMode || ""}</td>
  //         <td class="center">${p?.total?.toFixed(2) || "0.00"}</td>
  //       </tr>`,
  //       )
  //       .join("")
  //   : `
  //     <tr>
  //       <td>-</td>
  //       <td class="right">0.00</td>
  //     </tr>`;

  const paymentRows =
    balanceAmount < 0
      ? `
      <tr>
        <td class="center">CASH</td>
        <td class="right">${balanceAmount.toFixed(2)}</td>
      </tr>
    `
      : paymentList.length
        ? paymentList
            .map(
              (p) => `
          <tr>
            <td class="center">${p?.paymentMode || "-"}</td>
            <td class="right">${(p?.total ?? 0).toFixed(2)}</td>
          </tr>`,
            )
            .join("")
        : `
      <tr>
        <td class="center">-</td>
        <td class="right">0.00</td>
      </tr>
    `;

    
  /* ---------------------------------------------------------
     🔹 Replace Placeholders in Template
  --------------------------------------------------------- */
  template = template
    .replace(/{{reportName}}/g, reportName)

    /* Firm */
    .replace(/{{firmName}}/g, firm.name || "Sri Vedamaayu Ayurveda Hospital and Wellness Centre")
    .replace(/{{firmAddress}}/g, firm.address || "")
    .replace(/{{firmCity}}/g, firm.city || "")
    .replace(/{{firmPincode}}/g, firm.pincode || "")
    .replace(/{{firmPhone}}/g, firm.phone || "")
    .replace(/{{firmEmail}}/g, firm.email || "")
    .replace(/{{firmTin}}/g, firm.tin || "")
    .replace(/{{firmCIN}}/g, firm.cin || "")
    .replace(/{{firmStateCode}}/g, firm.stateCode || "")

    .replace(/{{section}}/g, customer.section || "")
    .replace(/{{patient}}/g, customer.patientName || "")
    .replace(/{{age}}/g, customer.age || 0)
    .replace(/{{address}}/g, customer.address || "")
    .replace(/{{roomName}}/g, customer.roomName || "")
    .replace(/{{doctor}}/g, customer.doctorName || "")
    .replace(/{{regNo}}/g, customer.regNumber || "")
    .replace(/{{ipNumber}}/g, customer.ipNumber || "")
    .replace(/{{phono}}/g, customer.phono || "")
    .replace(/{{patientId}}/g, customer.regNo || "")
    .replace(/{{doa}}/g, doa)
    .replace(/{{dod}}/g, dod)
    .replace(/{{dep}}/g, customer.dep || "")
    .replace(/{{plan}}/g, customer.insPlan || "")
    .replace(/{{billNo}}/g, customer.billNumber || "")
    .replace(/{{billDate}}/g, formatBillDate(customer.billDate))
    .replace(/{{total}}/g, summary.total?.toFixed(2) || "")
    .replace(/{{groupDiscount}}/g, summary.discount?.toFixed(2) || "")
    .replace(/{{billAmount}}/g, summary.billAmount?.toFixed(2) || "")
    .replace(/<!-- ADVANCEPAYMENT_ROW -->/g, advancePaymentAmount)
    .replace(/{{receivedAmount}}/g, summary.receivedAmount?.toFixed(2) || "")
    .replace(/{{payable}}/g, summary.payable?.toFixed(2) || "")
    .replace(/<!-- BALANCE_REFUND_ROW -->/g, balanceAndRefundRow)
    .replace(/{{amountWords}}/g, summary.billAmountInWords || "")
    .replace(/{{generatedBy}}/g, summary.generatedBy || "")
    .replace(/{{roundOff}}/g, summary.roundOff?.toFixed(2) || "")
    .replace(/<!-- finalTableHtml -->/g, finalTableHtml)
    .replace(/<!-- IP_NO_ROW -->/g, ipNoRow)
    .replace(/<!-- ADMISSION_VISIT -->/g, admissionVisitHtml)
    .replace(/<!-- INSURANCE_DETAILS -->/g, insuranceDetails)
    .replace(/{{taxrows}}/g, taxAmount)
    .replace(/{{narration}}/g, narration)
    .replace(/{{narration1}}/g, narration1)
    .replace(/<!-- PAYMENT_ROWS -->/g, paymentRows);

  if (reportName === "QUOTATION") {
    template = template
      .replace(/>\s*INVOICE\s*</g, ">QUOTATION<")
      .replace(/>\s*TAX INVOICE\s*</g, ">QUOTATION<")
      .replace(/Invoice No\./g, "Quote No.")
      .replace(/Invoice Date/g, "Quote Date");
  }

  return template;
};

export const generateMultiBillTemplate = async (dtoList, orgCode, advanceRefundRows = []) => {
  const orgPrefix = (orgCode || "MNH").toLowerCase();
  const templatePath = `/templates/${orgPrefix}_allinvoiceTemplate.html`;

  console.log("generateMultiBillTemplate : ", dtoList, orgCode, advanceRefundRows);
  // 1. Load template with fallback
  let response;
  try {
    response = await fetch(templatePath);
    if (!response.ok) throw new Error(`Template not found: ${templatePath}`);
  } catch (err) {
    console.warn(`Falling back to MNH multi template: ${err.message}`);
    const fallbackPath = `/templates/mnh_allinvoiceTemplate.html`;
    response = await fetch(fallbackPath);
  }

  let template = await response.text();

  // 2. Use FIRST dto for header / patient info
  const first = dtoList[0] || {};
  const firm = first.firmInfo || {};
  const customer = first.customerDetail || {};
  // const reportName = first.reportName || "";
  const getPatienttype = () => {
    if (customer.patientType === "InPatient") return "IP";
    if (customer.patientType === "OutPatient") return "OP";
    return "";
  };

  const reportName = `Annexure for ${getPatienttype()} Bill Services`;
  const doa = customer.doa && customer.doa !== "null" ? customer.doa : "";
  const dod = customer.dod && customer.dod !== "null" ? customer.dod : "";

  // IP / OP no row
  const showIpNo = customer.patientType === "InPatient";
  const showOpNo = customer.patientType === "OutPatient";

  const ipNoRow = showIpNo
    ? `
      <tr>
        <td><b>IP No</b></td>
        <td>: ${customer.ipNumber || ""}</td>
      </tr>`
    : showOpNo
      ? `
      <tr>
        <td><b>OP No</b></td>
        <td>: ${customer.ipNumber || ""}</td>
      </tr>`
      : "";

  // DOA/DOD vs Visit Date row
  let admissionVisitHtml = "";
  if (customer.patientType === "InPatient") {
    admissionVisitHtml = `
      <tr>
        <td><b>DOA</b></td>
        <td>: ${doa}</td>
      </tr>
      <tr>
        <td><b>DOD</b></td>
        <td>: ${dod}</td>
      </tr>`;
  } else if (customer.patientType === "OutPatient") {
    admissionVisitHtml = `
      <tr>
        <td><b>Visit Date</b></td>
        <td>: ${doa}</td>
      </tr>`;
  }

  // Insurance details if any
  let insuranceDetails = "";
  if (customer.insPlan && customer.insPlan.trim() !== "") {
    insuranceDetails = `
      ${
        customer.insurancePolicyNumber
          ? `
      <tr>
        <td><b>Policy No</b></td>
        <td>: ${customer.insurancePolicyNumber}</td>
      </tr>`
          : ""
      }
      ${
        customer.insuranceCardNumber
          ? `
      <tr>
        <td><b>Card No</b></td>
        <td>: ${customer.insuranceCardNumber}</td>
      </tr>`
          : ""
      }`;
  }

  // 3. Build ALL bill blocks
  // const billBlocksHtml = dtoList
  //   .map((dto) => {
  //     const cust = dto.customerDetail || {};
  //     const items = dto.billingItem || [];
  //     const summary = dto.billSummary || {};

  //     const rowsHtml = items
  //       .map((s) => {
  //         const sl = s.slNo || "";
  //         return `
  //           <tr>
  //             <td class="text-center">${sl}</td>
  //             <td>${s.serviceName || ""}</td>
  //             <td class="text-right">${s.charges?.toFixed(2) || ""}</td>
  //             <td class="text-center">${s.units || ""}</td>
  //             <td class="text-right">${s.gross?.toFixed(2) || ""}</td>
  //             <td class="text-right">${s.discount?.toFixed(2) || ""}</td>
  //             <td class="text-right">${s.netAmount?.toFixed(2) || ""}</td>
  //           </tr>`;
  //       })
  //       .join("");

  //     return `
  //       <div class="bill-block bill-box">

  //         <h4 style="margin:0; font-weight:bold; font-size:13px; text-decoration:underline;">
  //           ${cust.section || ""}
  //         </h4>

  //         <table style="margin-top:6px;">
  //           <tr>
  //             <td><b>Bill No.</b></td>
  //             <td>: ${cust.billNumber || ""}</td>
  //             <td style="width:80px;"></td>
  //             <td><b>Bill Date</b></td>
  //             <td>: ${formatBillDate(cust.billDate)}</td>
  //           </tr>
  //         </table>

  //         <table class="bordered mt-10">
  //           <thead>
  //             <tr>
  //               <th>Sl No</th>
  //               <th>Service Name</th>
  //               <th>Charges (Rs)</th>
  //               <th>Units</th>
  //               <th>Gross</th>
  //               <th>Discount</th>
  //               <th>Net Amount (Rs)</th>
  //             </tr>
  //           </thead>
  //           <tbody>
  //             ${rowsHtml}
  //             <tr>
  //               <td colspan="4" class="text-right"><b>Total (Rs)</b></td>
  //               <td class="text-right">${summary.total?.toFixed(2) || ""}</td>
  //               <td class="text-right">${summary.discount?.toFixed(2) || ""}</td>
  //               <td class="text-right">${summary.billAmount?.toFixed(2) || ""}</td>
  //             </tr>
  //           </tbody>
  //         </table>

  //         <p class="mt-10"><b>Amount in Words:</b> ${summary.billAmountInWords || ""}</p>
  //         <p style="text-align:right; margin-top:5px;">Generated : ${
  //           summary.generatedBy || ""
  //         }</p>
  //       </div>`;
  //   })
  //   .join("\n");
  const billBlocksHtml = dtoList
    .map((dto) => {
      const cust = dto.customerDetail || {};
      const items = dto.billingItem || [];
      const summary = dto.billSummary || {};

      const rowsHtml = items
        .map((s) => {
          const sl = s.slNo || "";
          return `
          <tr>
            <td class="text-center">${sl}</td>
            <td>${s.serviceName || ""}</td>
            <td class="text-right">${s.charges?.toFixed(2) || ""}</td>
            <td class="text-center">${s.units || ""}</td>
            <td class="text-right">${s.gross?.toFixed(2) || ""}</td>
            <td class="text-right">${s.discount?.toFixed(2) || ""}</td>
            <td class="text-right">${s.netAmount?.toFixed(2) || ""}</td>
          </tr>`;
        })
        .join("");

      return `
      <div class="bill-block bill-box">

        <!-- Department heading 
        <h4 style="margin:0 0 4px 0; font-weight:bold; font-size:13px;">
          ${cust.section || ""}
        </h4>-->

        <!-- Bill Dept / Bill No / Bill Date -->
        <table width="100%">
          <tr>
           <!-- <td style="width:20%;"><b>Bill Dept</b></td> -->
            <td colspan="2">
            <b>Bill Dept</b> : ${cust.section || ""}
            </td>
          </tr>
          <tr>
            <td colspan="2">
              <b>Bill No.</b> : ${cust.billNumber || ""}
            </td>
            <td style="text-align:right;">
              <b>Bill Date</b> : ${formatBillDate(cust.billDate)}
            </td>
          </tr>
        </table>

        <table class="bordered mt-10">
          <thead>
            <tr>
              <th>Sl No</th>
          ${
            ORG_CODES.VEDAMAAYU === orgCode
              ? "<th>Description</th>"
              : "<th>Service Name</th>"
          }
              <th>Charges (Rs)</th>
              <th>Units</th>
              <th>Gross</th>
              <th>Discount</th>
              <th>Net Amount (Rs)</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
            <tr>
              <td colspan="4" class="text-right"><b>Total (Rs)</b></td>
              <td class="text-right">${summary.total?.toFixed(2) || ""}</td>
              <td class="text-right">${summary.discount?.toFixed(2) || ""}</td>
              <td class="text-right">${summary.billAmount?.toFixed(2) || ""}</td>
            </tr>
          </tbody>
        </table>

        <p class="mt-10"><b>Amount in Words:</b>Rs ${
          summary.billAmountInWords || ""
        }Only</p>
        <p style="text-align:right; margin-top:5px;">Generated : ${
          summary.generatedBy || ""
        }</p>
      </div>`;
    })
    .join("\n");

  const receiptSectionHtml = buildReceiptSectionHtml(advanceRefundRows);

  const summaryHtml = buildAllInvoiceSummaryHtml(dtoList);
  // 4. Replace template placeholders
  template = template
    .replace(/{{reportName}}/g, reportName)
    .replace(/{{firmName}}/g, firm.FIRM_NAME || "")
    .replace(/{{firmAddress}}/g, firm.FIRM_ADDRESS || "")
    .replace(/{{firmCity}}/g, firm.FIRM_CITY || "")
    .replace(/{{firmPincode}}/g, firm.FIRM_PINCODE || "")
    .replace(/{{firmPhone}}/g, firm.FIRM_PHONE || "")
    .replace(/{{section}}/g, customer.section || "")
    .replace(/{{patient}}/g, customer.patientName || "")
    .replace(/{{age}}/g, customer.age || 0)
    .replace(/{{address}}/g, customer.address || "")
    .replace(/{{doctor}}/g, customer.doctorName || "")
    .replace(/{{regNo}}/g, customer.regNumber || "")
    .replace(/{{plan}}/g, customer.insPlan || "")
    .replace(/<!-- IP_NO_ROW -->/g, ipNoRow)
    .replace(/<!-- ADMISSION_VISIT -->/g, admissionVisitHtml)
    .replace(/<!-- INSURANCE_DETAILS -->/g, insuranceDetails)
    .replace(
      /{{billBlocks}}/g,
      billBlocksHtml + receiptSectionHtml + summaryHtml 
    );
  console.log("template ", template);
  return template;
};

const generateSummaryTemplate = async (dtoList, orgCode) => {
  const orgPrefix = (orgCode || "MNH").toLowerCase();
  const templatePath = `/templates/${orgPrefix}_invoicesummaryTemplate.html`;

  let response;
  try {
    response = await fetch(templatePath);
    if (!response.ok) throw new Error("Template missing");
  } catch (e) {
    response = await fetch("/templates/mnh_invoicesummaryTemplate.html");
  }

  let template = await response.text();

  const first = dtoList[0];
  const firm = first.firmInfo;
  const customer = first.customerDetail;

  // Build summary rows
  let summaryRows = "";
  let totalAmount = 0;

  dtoList.forEach((d) => {
    const cust = d.customerDetail;
    const summary = d.billSummary;

    totalAmount += summary.billAmount || 0;

    summaryRows += `
      <tr>
        <td>${cust.section}</td>
        <td>${cust.billNumber}</td>
        <td class="text-right">${summary.billAmount.toFixed(2)}</td>
      </tr>
    `;
  });

  // Replace fields in template
  template = template
    .replace(/{{firmName}}/g, firm.FIRM_NAME)
    .replace(/{{firmAddress}}/g, firm.FIRM_ADDRESS)
    .replace(/{{firmCity}}/g, firm.FIRM_CITY)
    .replace(/{{firmPincode}}/g, firm.FIRM_PINCODE)
    .replace(/{{firmPhone}}/g, firm.FIRM_PHONE)
    .replace(/{{section}}/g, customer.section)
    .replace(/{{patient}}/g, customer.patientName)
    .replace(/{{age}}/g, customer.age)
    .replace(/{{address}}/g, customer.address)
    .replace(/{{doctor}}/g, customer.doctorName)
    .replace(/{{regNo}}/g, customer.regNumber)
    .replace(/{{visitDate}}/g, customer.doa)
    .replace(/{{summaryRows}}/g, summaryRows)
    .replace(/{{totalAmount}}/g, totalAmount.toFixed(2));

  return template;
};
